import type { StreamVNextChunkType, WorkflowRunResult } from '@mastra/client-js';
import { useCallback, useRef, useState } from 'react';
import mastraClient, { mastraFetch } from '../lib/mastraClient';

export type WorkflowStatus = 'idle' | 'creating' | 'running' | 'streaming' | 'completed' | 'error';

export interface UseWorkflowOptions {
  workflowId: string;
  resourceId?: string;
}

export interface UseWorkflowState {
  status: WorkflowStatus;
  runId: string | null;
  result: WorkflowRunResult | null;
  streamChunks: StreamVNextChunkType[];
  error: Error | null;
}

export interface UseWorkflowReturn extends UseWorkflowState {
  /**
   * Creates a new workflow run and returns the runId.
   * Must be called before `start`, `startAsync` or `stream`.
   */
  createRun: (params?: { runId?: string }) => Promise<string>;

  /**
   * Starts the workflow run without waiting for completion.
   * Requires `createRun` to have been called first.
   */
  start: (inputData: Record<string, any>) => Promise<void>;

  /**
   * Starts the workflow run and waits for the full result.
   * Requires `createRun` to have been called first.
   * The result is stored in the `result` state.
   */
  startAsync: (inputData: Record<string, any>) => Promise<WorkflowRunResult>;

  /**
   * Starts the workflow run and monitors it via SSE stream.
   * Requires `createRun` to have been called first.
   * Chunks are accumulated in `streamChunks`.
   */
  stream: (inputData: Record<string, any>, onChunk?: (chunk: StreamVNextChunkType) => void) => Promise<void>;

  /**
   * Observes the stream for an already-running workflow run.
   * Requires `createRun` to have been called first.
   */
  observeStream: (onChunk?: (chunk: StreamVNextChunkType) => void) => Promise<void>;

  /** Cancels the current workflow run. */
  cancel: () => Promise<void>;

  /** Resets state back to idle. */
  reset: () => void;
}

const RECORD_SEPARATOR = '\x1E';

async function readStreamChunks(response: Response, onChunk: (chunk: StreamVNextChunkType) => void): Promise<void> {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split(RECORD_SEPARATOR);
    buffer = parts.pop() ?? '';

    for (const part of parts) {
      const trimmed = part.trim();
      if (trimmed) {
        try {
          onChunk(JSON.parse(trimmed) as StreamVNextChunkType);
        } catch {
          // skip malformed chunks
        }
      }
    }
  }

  if (buffer.trim()) {
    try {
      onChunk(JSON.parse(buffer.trim()) as StreamVNextChunkType);
    } catch {
      // skip
    }
  }
}

/**
 * Hook for interacting with a Mastra AI workflow.
 *
 * Flow:
 *   1. `createRun()` — registers a run on the server, returns runId
 *   2. `start(inputData)` — fires-and-forgets the run (no streaming)
 *      OR
 *      `startAsync(inputData)` — runs and waits for the full result
 *      OR
 *      `stream(inputData, onChunk?)` — runs and subscribes to the SSE stream
 */
export const useWorkflow = ({ workflowId, resourceId }: UseWorkflowOptions): UseWorkflowReturn => {
  const [status, setStatus] = useState<WorkflowStatus>('idle');
  const [runId, setRunId] = useState<string | null>(null);
  const [result, setResult] = useState<WorkflowRunResult | null>(null);
  const [streamChunks, setStreamChunks] = useState<StreamVNextChunkType[]>([]);
  const [error, setError] = useState<Error | null>(null);

  // Ref to avoid stale closure issues in callbacks
  const runIdRef = useRef<string | null>(null);

  const createRun = useCallback(
    async (params?: { runId?: string }): Promise<string> => {
      setStatus('creating');
      setError(null);
      setStreamChunks([]);
      setResult(null);

      try {
        const client = await mastraClient();
        const workflow = client.getWorkflow(workflowId);
        const run = await workflow.createRun({ runId: params?.runId, resourceId });

        runIdRef.current = run.runId;
        setRunId(run.runId);
        setStatus('idle');
        return run.runId;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        setStatus('error');
        throw e;
      }
    },
    [workflowId, resourceId]
  );

  const start = useCallback(
    async (inputData: Record<string, any>): Promise<void> => {
      if (!runIdRef.current) throw new Error('Call createRun() before start()');

      setStatus('running');
      setError(null);

      try {
        await mastraFetch(`/api/workflows/${workflowId}/start?runId=${runIdRef.current}`, {
          method: 'POST',
          body: { inputData },
        });
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        setStatus('error');
        throw e;
      }
    },
    [workflowId]
  );

  const startAsync = useCallback(
    async (inputData: Record<string, any>): Promise<WorkflowRunResult> => {
      if (!runIdRef.current) throw new Error('Call createRun() before startAsync()');

      setStatus('running');
      setError(null);

      try {
        const response = await mastraFetch(`/api/workflows/${workflowId}/start-async?runId=${runIdRef.current}`, {
          method: 'POST',
          body: { inputData },
        });

        const runResult: WorkflowRunResult = await response.json();
        setResult(runResult);
        setStatus('completed');
        return runResult;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        setStatus('error');
        throw e;
      }
    },
    [workflowId]
  );

  const stream = useCallback(
    async (inputData: Record<string, any>, onChunk?: (chunk: StreamVNextChunkType) => void): Promise<void> => {
      if (!runIdRef.current) throw new Error('Call createRun() before stream()');

      setStatus('streaming');
      setError(null);
      setStreamChunks([]);

      try {
        const response = await mastraFetch(`/api/workflows/${workflowId}/stream?runId=${runIdRef.current}`, {
          method: 'POST',
          body: { inputData },
          stream: true,
        });

        await readStreamChunks(response, (chunk) => {
          setStreamChunks((prev) => [...prev, chunk]);
          onChunk?.(chunk);
        });

        setStatus('completed');
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        setStatus('error');
        throw e;
      }
    },
    [workflowId]
  );

  const observeStream = useCallback(
    async (onChunk?: (chunk: StreamVNextChunkType) => void): Promise<void> => {
      if (!runIdRef.current) throw new Error('Call createRun() before observeStream()');

      setStatus('streaming');
      setError(null);

      try {
        const response = await mastraFetch(`/api/workflows/${workflowId}/observe-stream?runId=${runIdRef.current}`, {
          method: 'POST',
          stream: true,
        });

        await readStreamChunks(response, (chunk) => {
          setStreamChunks((prev) => [...prev, chunk]);
          onChunk?.(chunk);
        });

        setStatus('completed');
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        setStatus('error');
        throw e;
      }
    },
    [workflowId]
  );

  const cancel = useCallback(async (): Promise<void> => {
    if (!runIdRef.current) return;

    try {
      await mastraFetch(`/api/workflows/${workflowId}/runs/${runIdRef.current}`, {
        method: 'DELETE',
      });
      setStatus('idle');
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      setStatus('error');
      throw e;
    }
  }, [workflowId]);

  const reset = useCallback((): void => {
    runIdRef.current = null;
    setStatus('idle');
    setRunId(null);
    setResult(null);
    setStreamChunks([]);
    setError(null);
  }, []);

  return {
    status,
    runId,
    result,
    streamChunks,
    error,
    createRun,
    start,
    startAsync,
    stream,
    observeStream,
    cancel,
    reset,
  };
};

export default useWorkflow;
